/**
 * Convert any image file (including HEIC) to JPEG using browser canvas.
 * This ensures all uploaded images are in a format compatible with all AI vision APIs.
 */
export async function convertToJpeg(file: File, quality = 0.85): Promise<File> {
  // Already JPEG — return as-is
  if (file.type === 'image/jpeg') {
    return file;
  }

  // For formats the browser can render (PNG, WebP, and HEIC on Safari/iOS),
  // draw to canvas and export as JPEG
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Cap dimensions to 2048px to keep file size reasonable
      const maxDim = 2048;
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to convert image'));
            return;
          }

          // Create a new File with .jpg extension
          const baseName = file.name.replace(/\.[^.]+$/, '');
          const converted = new File([blob], `${baseName}.jpg`, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(converted);
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      // If browser can't decode (e.g., HEIC on Chrome desktop), return original
      // The S3 upload will still work, and we handle it server-side
      console.warn(`Browser cannot decode ${file.type}, uploading as-is`);
      resolve(file);
    };

    img.src = url;
  });
}
